import { normalizeJourneyStage, validateJourneyStageExplanation } from './journeyStageExplanation.js';

const JOURNEY_BATCH6_EXPLANATIONS = Object.freeze({
  'inpatient-admission': Object.freeze({
    'before-admission': Object.freeze({
      linkIds: Object.freeze(['admission', 'medicines']),
      questionIndexes: Object.freeze([0, 1, 2]),
      roleIds: Object.freeze([
        'consultant',
        'resident_doctor_previously_referred_to_as_junior_doctor',
        'staff_nurse',
        'healthcare_assistant',
        'pharmacist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before or when you arrive for a planned inpatient admission, staff will confirm why you are coming into hospital. They will tell you where to go and check your usual medicines, allergies and support needs. They may check your blood pressure, pulse and temperature. Depending on the reason for admission, you may also have blood tests or other checks.',
        whyItMayHappen: 'These checks help the team make sure the plan is still appropriate, identify anything that could affect treatment and make the first part of your hospital stay as safe and organised as possible.',
        whatTeamIsDoing: 'The team is confirming your medical history and current plan, reviewing medicines, checking for any changes since the admission was arranged and deciding what needs to happen first once you are on the ward.',
        variationNote: 'Planned admissions can differ between services. The order of tests, reviews and treatments may change if your clinical needs change or if the hospital needs to respond to urgent pressures. If an important part of the plan changes, the team should explain why.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'settle-on-ward',
            title: 'Settle onto the ward and continue the planned care',
            stageId: 'during-stay',
            when: 'When the admission checks are complete and the planned stay can go ahead',
          }),
          Object.freeze({
            id: 'more-assessment',
            title: 'Have more assessment before treatment starts',
            stageId: 'during-stay',
            description: 'The team may need extra tests, a specialist review or time to clarify medicines before treatment begins.',
            when: 'If something needs to be checked before the planned treatment or investigation',
          }),
          Object.freeze({
            id: 'change-admission-plan',
            title: 'Change or rearrange the admission plan',
            when: 'If it would not be safe or appropriate to continue with the original plan that day',
          }),
        ]),
      }),
    }),
    'during-stay': Object.freeze({
      linkIds: Object.freeze(['ward']),
      questionIndexes: Object.freeze([3, 4]),
      roleIds: Object.freeze([
        'consultant',
        'resident_doctor_previously_referred_to_as_junior_doctor',
        'staff_nurse',
        'pharmacist',
        'physiotherapist',
        'occupational_therapist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'During your stay, nurses and doctors will regularly review how you are. They may give or adjust treatment and keep you informed about the plan. You may hear terms such as ward round, handover or care plan. Pharmacists, physiotherapists, occupational therapists, dietitians or other specialists may also be involved.',
        whyItMayHappen: 'Hospital care often changes as new information becomes available. Regular reviews help the team see whether treatment is working, whether more tests are needed and what support will help you stay as mobile and independent as possible.',
        whatTeamIsDoing: 'The team is monitoring your condition, sharing important information between shifts and professions, reviewing medicines and results, and updating the plan with you. They may also begin discharge planning before your treatment is completely finished.',
        variationNote: 'The people you see can change from day to day, and not every profession is involved in every admission. A ward round may be brief, so if you have questions it can help to write them down or ask a nurse when the best time is to discuss them.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-inpatient-care',
            title: 'Continue treatment and review on the ward',
            stageId: 'during-stay',
            when: 'If you still need hospital treatment, monitoring or assessment',
          }),
          Object.freeze({
            id: 'specialist-review',
            title: 'Have another specialist review or further tests',
            stageId: 'during-stay',
            description: 'Another team may be asked to review you if a new question arises or more specialist input is needed.',
            when: 'If your results, symptoms or recovery mean the plan needs more information',
          }),
          Object.freeze({
            id: 'start-discharge-planning',
            title: 'Move towards discharge planning',
            stageId: 'leaving-hospital',
            when: 'When hospital-level treatment is no longer needed or the remaining care can be provided elsewhere',
          }),
        ]),
      }),
    }),
    'leaving-hospital': Object.freeze({
      linkIds: Object.freeze(['discharge']),
      questionIndexes: Object.freeze([5, 6]),
      roleIds: Object.freeze([
        'staff_nurse',
        'pharmacist',
        'physiotherapist',
        'occupational_therapist',
        'discharge_coordinator',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Before you leave hospital, the team should explain your discharge plan and any changes to your medicines. They should explain follow-up and who to contact if you are worried. You may receive take-home medicines and a discharge summary. If you need equipment, therapy, nursing or other support, the team may arrange this before you leave.',
        whyItMayHappen: 'Leaving hospital safely depends on more than the medical treatment being finished. The team also needs to consider medicines, mobility, practical support, transport, follow-up and whether the next part of your care is ready.',
        whatTeamIsDoing: 'The team is checking whether you are medically fit for discharge, completing prescriptions and paperwork, arranging referrals or equipment where needed and making sure the next services involved have the information they require.',
        variationNote: 'Being medically fit for discharge means you no longer need the same level of hospital treatment; it does not always mean you can leave immediately. Practical arrangements or support may still need to be completed. Some people go home, while others move to rehabilitation or another care setting.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'home',
            title: 'Go home with the agreed plan and support',
            when: 'When it is safe for your care to continue at home',
          }),
          Object.freeze({
            id: 'rehab-or-care-setting',
            title: 'Move to rehabilitation or another care setting',
            when: 'If you no longer need acute hospital care but still need more support than can be provided at home',
          }),
          Object.freeze({
            id: 'wait-for-arrangements',
            title: 'Remain in hospital while essential arrangements are completed',
            stageId: 'leaving-hospital',
            when: 'If an important part of the discharge plan is not yet ready',
          }),
        ]),
      }),
    }),
  }),

  'discharge-recovery': Object.freeze({
    'getting-ready': Object.freeze({
      linkIds: Object.freeze(['discharge', 'medicines']),
      questionIndexes: Object.freeze([0, 1, 2]),
      roleIds: Object.freeze([
        'staff_nurse',
        'pharmacist',
        'physiotherapist',
        'occupational_therapist',
        'discharge_coordinator',
      ]),
      explanation: Object.freeze({
        whatHappens: 'As you get ready to leave hospital, the team will confirm what needs to happen before discharge. They will explain any medicine changes. They will check whether you need equipment, transport or support at home. You may receive take-home medicines, a discharge summary and follow-up information.',
        whyItMayHappen: 'A clear discharge plan helps reduce gaps between hospital and home. It makes it easier to know what has changed, what you need to do next and who is responsible for any ongoing treatment or support.',
        whatTeamIsDoing: 'Staff are checking that the medical and practical parts of the plan fit together. This can include prescriptions, mobility or equipment checks, referrals to community services and making sure you know what symptoms should prompt you to seek advice.',
        variationNote: 'Discharge arrangements vary a lot. Some people need very little support, while others need therapy, nursing, equipment or another care setting. Being medically fit for discharge does not always mean every practical arrangement is already complete.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'go-home',
            title: 'Leave hospital and continue recovery at home',
            stageId: 'first-days-home',
            when: 'When the team and you are satisfied that the agreed plan can be followed safely at home',
          }),
          Object.freeze({
            id: 'more-support',
            title: 'Arrange more support before leaving',
            stageId: 'getting-ready',
            description: 'This may include equipment, therapy, community nursing, transport or help with medicines.',
            when: 'If an essential part of the discharge plan is not yet in place',
          }),
          Object.freeze({
            id: 'other-care-setting',
            title: 'Move to rehabilitation or another care setting',
            when: 'If you no longer need acute hospital care but cannot yet manage safely at home',
          }),
        ]),
      }),
    }),
    'first-days-home': Object.freeze({
      linkIds: Object.freeze(['recovery']),
      questionIndexes: Object.freeze([3, 4]),
      roleIds: Object.freeze([
        'district_nurse',
        'physiotherapist',
        'occupational_therapist',
        'pharmacist',
        'general_practitioner_gp',
      ]),
      explanation: Object.freeze({
        whatHappens: 'The first few days at home can feel different from being in hospital. You may feel tired, have less appetite, have pain or feel less confident with normal activities. Follow the instructions you were given about medicines, activity, eating and drinking. Also follow any advice about wound or device care, exercises or equipment.',
        whyItMayHappen: 'Recovery often continues after you leave hospital. Your body may still be recovering from illness, treatment, reduced activity or a procedure, and it can take time to return to your usual routine.',
        whatTeamIsDoing: 'Hospital and community teams may be handing over different parts of your care. Depending on your needs, your GP, community or district nurse, pharmacist or therapy team may review you or provide support after discharge.',
        variationNote: 'Recovery is different for everyone. Some symptoms are expected during recovery, while others need earlier advice. Use the safety-netting information you were given and do not wait for a routine appointment if you develop a problem the team told you needs prompt assessment.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'continue-recovery',
            title: 'Continue the planned recovery at home',
            stageId: 'first-days-home',
            when: 'If you are improving and the discharge plan is working as expected',
          }),
          Object.freeze({
            id: 'community-review',
            title: 'Have support from your GP or community team',
            stageId: 'first-days-home',
            when: 'If you need help with medicines, nursing care, mobility, equipment or another part of recovery',
          }),
          Object.freeze({
            id: 'earlier-help',
            title: 'Seek earlier advice if a problem develops',
            description: 'Use the contact and safety-netting instructions in your discharge information, or seek urgent help when appropriate.',
            when: 'If symptoms become worse, something new develops or you are unable to follow the plan safely',
          }),
        ]),
      }),
    }),
    'follow-up': Object.freeze({
      linkIds: Object.freeze(['follow-up']),
      questionIndexes: Object.freeze([5, 6]),
      roleIds: Object.freeze([
        'consultant',
        'general_practitioner_gp',
        'physiotherapist',
        'occupational_therapist',
        'district_nurse',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Follow-up may involve a hospital appointment, GP review, therapy, repeat tests, results or a phone or video review. The purpose is to check recovery, review treatment and decide whether any further care is needed.',
        whyItMayHappen: 'Some problems need planned review after hospital, while others can be managed by primary care or community services. Follow-up also gives you a chance to discuss ongoing symptoms, progress and questions about returning to usual activities.',
        whatTeamIsDoing: 'The team is reviewing whether recovery is going as expected, acting on results, checking that medicines and support remain appropriate and deciding when specialist follow-up can reduce or finish.',
        variationNote: 'Not everyone needs a hospital follow-up appointment. The type and timing of review depend on why you were admitted and what treatment you had. Make sure you know which appointments or referrals have actually been arranged and who to contact if you have not heard when expected.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'specialist-follow-up',
            title: 'Continue specialist follow-up',
            stageId: 'follow-up',
            when: 'If the hospital team still needs to review recovery, results or treatment',
          }),
          Object.freeze({
            id: 'primary-community-care',
            title: 'Continue care with primary or community services',
            when: 'If regular hospital specialist follow-up is no longer needed',
          }),
          Object.freeze({
            id: 'earlier-review',
            title: 'Ask for an earlier review if recovery is not going as expected',
            when: 'If symptoms persist, worsen or a new concern develops before the planned review',
          }),
        ]),
      }),
    }),
  }),

  orthopaedics: Object.freeze({
    assessment: Object.freeze({
      linkIds: Object.freeze(['appointment']),
      questionIndexes: Object.freeze([0, 1, 2]),
      roleIds: Object.freeze([
        'surgeon',
        'radiographer',
        'physiotherapist',
        'clinical_nurse_specialist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Orthopaedic assessment usually focuses on pain, movement, strength and how a problem affecting a bone, joint or the tissues around them is affecting everyday life. The team may review X-rays or other scans and may suggest physiotherapy, medicines, an injection, further tests or surgery depending on the problem.',
        whyItMayHappen: 'The same symptom can have different causes, and the best treatment depends on the diagnosis, how severe the problem is, how much it affects you and what improvement you are hoping for.',
        whatTeamIsDoing: 'The team is combining your history, examination and imaging to understand the problem, checking whether non-surgical treatment is suitable and discussing the likely benefits and risks if surgery is one of the options.',
        variationNote: 'Seeing an orthopaedic surgeon does not mean you will need an operation. Many conditions are treated without surgery. If surgery is being considered, you may need pre-assessment and other checks before a final plan is confirmed.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'non-surgical-treatment',
            title: 'Start or continue treatment without surgery',
            stageId: 'treatment',
            when: 'If physiotherapy, medicines, an injection, activity changes or monitoring are the most appropriate next step',
          }),
          Object.freeze({
            id: 'surgery-plan',
            title: 'Plan an operation',
            stageId: 'treatment',
            when: 'If surgery is likely to help and you decide to proceed after discussing the benefits, risks and alternatives',
          }),
          Object.freeze({
            id: 'more-assessment',
            title: 'Have more tests or another specialist review',
            stageId: 'assessment',
            when: 'If the diagnosis or best treatment option is not yet clear',
          }),
        ]),
      }),
    }),
    treatment: Object.freeze({
      linkIds: Object.freeze(['surgery']),
      questionIndexes: Object.freeze([3, 4]),
      roleIds: Object.freeze([
        'surgeon',
        'anaesthetist',
        'staff_nurse',
        'operating_department_practitioner',
        'physiotherapist',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Treatment may be non-surgical or may involve an operation. If you have surgery, the team will confirm the procedure and anaesthetic plan, manage pain and start recovery afterwards. You may be encouraged to move or begin exercises quite soon, although the exact instructions depend on the operation or injury.',
        whyItMayHappen: 'Orthopaedic treatment aims to reduce pain, improve movement or function, help an injury heal correctly or prevent further problems. Surgery is used when the expected benefit is greater than the risks and other options are less suitable.',
        whatTeamIsDoing: 'The team is carrying out the agreed treatment, controlling pain, checking the affected limb or joint and planning how you can move safely. After surgery they may also use measures to reduce the risk of blood clots and other complications.',
        variationNote: 'Recovery instructions are not the same after every orthopaedic treatment. How quickly you can move, use the affected limb or go home depends on the procedure, your health and how safely you can manage day-to-day activities.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'rehabilitation',
            title: 'Begin rehabilitation and build up movement',
            stageId: 'rehabilitation',
            when: 'When the team is satisfied that it is safe to begin the next phase of recovery',
          }),
          Object.freeze({
            id: 'continue-hospital-care',
            title: 'Continue hospital treatment or observation',
            stageId: 'treatment',
            when: 'If you still need pain control, monitoring, treatment or help with mobility before moving on',
          }),
          Object.freeze({
            id: 'home-after-treatment',
            title: 'Go home with a recovery plan',
            stageId: 'rehabilitation',
            when: 'If you can manage safely and the support you need is in place',
          }),
        ]),
      }),
    }),
    rehabilitation: Object.freeze({
      linkIds: Object.freeze(['rehab', 'follow-up']),
      questionIndexes: Object.freeze([5, 6]),
      roleIds: Object.freeze([
        'physiotherapist',
        'occupational_therapist',
        'staff_nurse',
        'surgeon',
      ]),
      explanation: Object.freeze({
        whatHappens: 'Rehabilitation may include exercises, gradually increasing activity, learning to use walking aids or equipment and advice about returning to everyday tasks. If you had surgery, you may also need wound checks, medicines and follow-up with the orthopaedic team.',
        whyItMayHappen: 'Building movement and strength safely can help recovery, reduce loss of function and lower the risk of complications. The pace of rehabilitation needs to match the injury or operation and your own starting point.',
        whatTeamIsDoing: 'Physiotherapists and occupational therapists may assess mobility, exercises, stairs, equipment and daily activities. The wider team reviews pain, medicines, wound healing if relevant and any steps needed to reduce the risk of blood clots.',
        variationNote: 'Orthopaedic recovery can take very different amounts of time. Some people recover with exercises at home, while others need community or inpatient rehabilitation. Follow the movement and activity instructions given for your particular condition rather than comparing your progress with someone else’s.',
        nextSteps: Object.freeze([
          Object.freeze({
            id: 'home-rehabilitation',
            title: 'Continue rehabilitation at home or in the community',
            stageId: 'rehabilitation',
            when: 'If you can recover safely outside hospital with the exercises, equipment and support arranged',
          }),
          Object.freeze({
            id: 'orthopaedic-follow-up',
            title: 'Attend orthopaedic follow-up',
            stageId: 'rehabilitation',
            when: 'If the team needs to review healing, movement, an X-ray, a wound or the result of treatment',
          }),
          Object.freeze({
            id: 'earlier-review',
            title: 'Ask for earlier advice if recovery changes',
            description: 'Use the safety-netting advice given by your team if pain, swelling, wound problems, mobility or another symptom changes in a way they told you needs prompt review.',
            when: 'If recovery is not following the expected pattern or a new concern develops',
          }),
        ]),
      }),
    }),
  }),
});

function patientSummary(explanation) {
  return [
    explanation?.whatHappens,
    explanation?.whyItMayHappen,
    explanation?.whatTeamIsDoing,
    explanation?.variationNote,
  ].filter(Boolean).join(' ');
}

export function applyJourneyBatch6Explanation(templateId, stages = []) {
  const stageMap = JOURNEY_BATCH6_EXPLANATIONS[templateId];
  if (!stageMap) return stages;
  const knownStageIds = stages.map((stage) => stage.id);
  return stages.map((stage) => {
    const enrichment = stageMap[stage.id];
    if (!enrichment) return stage;
    const enriched = normalizeJourneyStage({
      ...stage,
      ...enrichment,
      description: patientSummary(enrichment.explanation),
    });
    const errors = validateJourneyStageExplanation(enriched, knownStageIds);
    if (errors.length) throw new Error(`${templateId} stage ${stage.id}: ${errors.join('; ')}`);
    return enriched;
  });
}

export { JOURNEY_BATCH6_EXPLANATIONS };
